"use client";

import { useId, useState } from "react";
import { ListChecks, Terminal } from "lucide-react";

interface Role {
  name: string;
  handsOn: string;
  questions: string;
}

const roles: Role[] = [
  {
    name: "Penetration Testers",
    handsOn:
      "Exploit live web targets and cloud environments end to end, from recon to proof of access.",
    questions:
      "Methodology, tooling choices, and reporting judgment under time pressure.",
  },
  {
    name: "SOC Analysts",
    handsOn:
      "Triage real alerts and hunt through live logs to separate noise from an actual intrusion.",
    questions: "Detection logic, log analysis, and escalation decisions.",
  },
  {
    name: "Cloud Security Engineers",
    handsOn: "Find and fix real misconfigurations in live Azure and AWS labs.",
    questions: "IAM design, network segmentation, and hardening trade-offs.",
  },
  {
    name: "DFIR Specialists",
    handsOn:
      "Work disk, memory, and log artifacts from a compromised host to reconstruct the attack timeline.",
    questions: "Forensic process, evidence handling, and containment calls.",
  },
  {
    name: "Red Teamers",
    handsOn:
      "Chain identity and cloud attack paths through a realistic environment without tripping defenses.",
    questions: "Tradecraft, OPSEC, and objective prioritization.",
  },
  {
    name: "AI Security Roles",
    handsOn:
      "Attack a live LLM application with prompt injection, jailbreaks, and data exfiltration attempts.",
    questions: "AI threat modeling and practical mitigations.",
  },
];

export function RoleAssessments() {
  const panelId = useId();
  const [open, setOpen] = useState<string | null>(null);
  // Keeps the last opened role rendered so the collapse animation has content
  const [shown, setShown] = useState<Role>(roles[0]);

  const toggle = (role: Role) => {
    if (open === role.name) {
      setOpen(null);
    } else {
      setShown(role);
      setOpen(role.name);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {roles.map((role) => {
          const active = open === role.name;
          return (
            <button
              key={role.name}
              type="button"
              onClick={() => toggle(role)}
              aria-expanded={active}
              aria-controls={panelId}
              className={`inline-flex min-h-11 items-center rounded-full border px-4 py-2 text-xs font-medium cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50 active:scale-[0.98] ${
                active
                  ? "border-cb-violet/50 bg-cb-violet/10 text-cb-violet-text"
                  : "border-cb-border bg-cb-surface-2 text-cb-muted hover:border-cb-violet/40 hover:text-cb-text"
              }`}
            >
              {role.name}
            </button>
          );
        })}
      </div>
      <div
        id={panelId}
        role="region"
        aria-label={`How ProofRange assesses ${shown.name}`}
        className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div
            key={shown.name}
            className="mt-4 rounded-xl border border-cb-violet/25 bg-cb-surface p-6 [animation:cb-fade-up_0.3s_ease-out_both] motion-reduce:animate-none"
          >
            <p className="text-sm font-semibold text-cb-text mb-4">
              How ProofRange verifies {shown.name}
            </p>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cb-violet/10 border border-cb-violet/25">
                  <Terminal className="h-4 w-4 text-cb-violet-text" />
                </div>
                <div>
                  <p className="text-sm font-medium text-cb-text mb-1">
                    Hands-on challenges
                  </p>
                  <p className="text-sm text-cb-muted leading-relaxed">
                    {shown.handsOn}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cb-violet/10 border border-cb-violet/25">
                  <ListChecks className="h-4 w-4 text-cb-violet-text" />
                </div>
                <div>
                  <p className="text-sm font-medium text-cb-text mb-1">
                    Targeted questions
                  </p>
                  <p className="text-sm text-cb-muted leading-relaxed">
                    {shown.questions}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
